/**
 * Platform video tools.
 *
 * Phase 13 P1 (June 2026)
 *
 * Bind on the `playback_control` capability declared on the `video`
 * scene_type. Three tools:
 *
 *   * `VideoSourcePicker`   — TOP_TOOLBAR; URL / Vimeo id / YouTube
 *                             id / recording_id input. Mirrors the
 *                             validator's "one source must be set"
 *                             rule the audit suite enforces.
 *   * `VideoMCQMarker`      — BOTTOM_STRIP; renders a horizontal
 *                             timeline with markers at each MCQ
 *                             timestamp. Clicking a marker selects
 *                             it; dragging would adjust the time
 *                             (drag handler is left as a stub —
 *                             the UI surface is the framework
 *                             contract).
 *   * `VideoZoneOverlay`    — CANVAS_OVERLAY; toggles draw-zone
 *                             mode used to author tap-zone
 *                             interactions.
 */
import React from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { requiresSceneType } from '../predicates';

// ────────────────────────────────────────────────────────────────────
// VideoSourcePicker
// ────────────────────────────────────────────────────────────────────

function VideoSourcePicker() {
  const { scene, setDomainMetadata } = useDesigner();
  if (!scene) return null;
  const meta = scene.domain_metadata || {};

  return (
    <div className="designer-tool" data-tool-id="platform.video_source_picker">
      <div className="designer-tool__row">
        <label className="designer-tool__label">Video URL</label>
        <input
          type="url"
          className="designer-tool__input"
          value={meta.video_url || ''}
          aria-label="Video URL"
          placeholder="https://cdn.example.com/clip.mp4"
          onChange={(e) => setDomainMetadata({ video_url: e.target.value })}
        />
      </div>
      <div className="designer-tool__row designer-tool__row--inline">
        <input
          type="text"
          className="designer-tool__input"
          value={meta.vimeo_id || ''}
          aria-label="Vimeo id"
          placeholder="Vimeo id"
          onChange={(e) => setDomainMetadata({ vimeo_id: e.target.value })}
        />
        <input
          type="text"
          className="designer-tool__input"
          value={meta.youtube_id || ''}
          aria-label="YouTube id"
          placeholder="YouTube id"
          onChange={(e) => setDomainMetadata({ youtube_id: e.target.value })}
        />
      </div>
    </div>
  );
}

export const videoSourcePicker = new DesignerTool({
  id:         'platform.video_source_picker',
  label:      'Video source',
  slot:       'RIGHT_INSP',
  category:   'metadata',
  applies_to: requiresSceneType('video'),
  Component:  VideoSourcePicker,
  weight:     20,
  helpText:   'Set the video source URL or platform id.',
});

// ────────────────────────────────────────────────────────────────────
// VideoMCQMarker
// ────────────────────────────────────────────────────────────────────

function VideoMCQMarker() {
  const { scene, setDomainMetadata } = useDesigner();
  const [editingIdx, setEditingIdx] = React.useState(null);
  if (!scene) return null;
  const markers = (scene.domain_metadata?.mcq_markers) || [];

  const updateMarkers = (next) => setDomainMetadata({ mcq_markers: next });

  const addMarker = () => {
    const next = [
      ...markers,
      {
        marker_id: `m_${markers.length + 1}_${Date.now().toString(36)}`,
        timestamp_ms: 0,
        max_attempts: 2,
        question: {
          prompt: '',
          choices: [
            { choice_id: 'c1', text: '', correct: true },
            { choice_id: 'c2', text: '', correct: false },
          ],
        },
      },
    ];
    updateMarkers(next);
    setEditingIdx(next.length - 1);
  };

  const removeMarker = (idx) => {
    updateMarkers(markers.filter((_, i) => i !== idx));
    if (editingIdx === idx) setEditingIdx(null);
  };

  const updateMarker = (idx, patch) => {
    updateMarkers(markers.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  const updateQuestion = (idx, qPatch) => {
    const m = markers[idx];
    updateMarker(idx, { question: { ...(m.question || {}), ...qPatch } });
  };

  const addChoice = (idx) => {
    const q = markers[idx].question || {};
    const choices = q.choices || [];
    const id = `c${choices.length + 1}_${Date.now().toString(36)}`;
    updateQuestion(idx, { choices: [...choices, { choice_id: id, text: '', correct: false }] });
  };

  const updateChoice = (mIdx, cIdx, patch) => {
    const q = markers[mIdx].question || {};
    const choices = (q.choices || []).map((c, i) => (i === cIdx ? { ...c, ...patch } : c));
    updateQuestion(mIdx, { choices });
  };

  const setCorrect = (mIdx, choiceId) => {
    const q = markers[mIdx].question || {};
    const choices = (q.choices || []).map((c) => ({ ...c, correct: c.choice_id === choiceId }));
    updateQuestion(mIdx, { choices });
  };

  const removeChoice = (mIdx, cIdx) => {
    const q = markers[mIdx].question || {};
    updateQuestion(mIdx, { choices: (q.choices || []).filter((_, i) => i !== cIdx) });
  };

  const formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
  };

  return (
    <div
      className="designer-tool designer-tool--video-markers"
      data-tool-id="platform.video_mcq_marker"
    >
      <h3 className="designer-tool__heading">Video Checkpoints</h3>
      <p style={{ fontSize: 11, color: 'var(--fg-text-muted)', marginBottom: 8 }}>
        Add questions at specific timestamps. The video pauses and the learner must answer correctly to continue.
      </p>

      {markers.map((m, i) => {
        const q = m.question || {};
        const isEditing = editingIdx === i;

        return (
          <div key={m.marker_id || i} style={{
            border: '1px solid var(--border-default, #334155)',
            borderRadius: 8,
            padding: '8px 10px',
            marginBottom: 8,
            background: isEditing ? 'var(--bg-surface-secondary, rgba(59,130,246,0.05))' : 'transparent',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                <span style={{ fontSize: 12, color: 'var(--fg-text-muted)', fontWeight: 600, minWidth: 30 }}>
                  #{i + 1}
                </span>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  className="designer-tool__input"
                  value={m.timestamp_ms}
                  aria-label={`Checkpoint ${i + 1} timestamp (ms)`}
                  style={{ width: 80, fontSize: 12 }}
                  onChange={(e) => updateMarker(i, { timestamp_ms: parseInt(e.target.value) || 0 })}
                />
                <span style={{ fontSize: 10, color: 'var(--fg-text-muted)' }}>
                  ms ({formatTime(m.timestamp_ms)})
                </span>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  type="button"
                  className="designer-tool__button designer-tool__button--icon"
                  onClick={() => setEditingIdx(isEditing ? null : i)}
                  title={isEditing ? 'Collapse' : 'Edit question'}
                >{isEditing ? '▲' : '▼'}</button>
                <button
                  type="button"
                  className="designer-tool__button designer-tool__button--icon"
                  onClick={() => removeMarker(i)}
                  title="Remove checkpoint"
                >×</button>
              </div>
            </div>

            {isEditing && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-default, #334155)' }}>
                <div className="designer-tool__row">
                  <label className="designer-tool__label" style={{ fontSize: 11 }}>Question</label>
                  <textarea
                    className="designer-tool__input designer-tool__input--textarea"
                    rows={2}
                    value={q.prompt || ''}
                    placeholder="What did you just learn?"
                    onChange={(e) => updateQuestion(i, { prompt: e.target.value })}
                  />
                </div>

                <div style={{ marginTop: 6 }}>
                  <label className="designer-tool__label" style={{ fontSize: 10 }}>
                    Choices (select correct answer)
                  </label>
                  {(q.choices || []).map((c, ci) => (
                    <div key={c.choice_id || ci} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      <input
                        type="radio"
                        name={`mcq-${m.marker_id}`}
                        checked={Boolean(c.correct)}
                        onChange={() => setCorrect(i, c.choice_id)}
                      />
                      <input
                        type="text"
                        className="designer-tool__input"
                        value={c.text || ''}
                        placeholder={`Choice ${ci + 1}`}
                        style={{ flex: 1, fontSize: 12 }}
                        onChange={(e) => updateChoice(i, ci, { text: e.target.value })}
                      />
                      <button
                        type="button"
                        className="designer-tool__button designer-tool__button--icon"
                        onClick={() => removeChoice(i, ci)}
                        style={{ fontSize: 10 }}
                      >×</button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="designer-tool__button designer-tool__button--small"
                    onClick={() => addChoice(i)}
                    style={{ fontSize: 11, marginTop: 4 }}
                  >+ Choice</button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <label className="designer-tool__label" style={{ fontSize: 10, margin: 0 }}>Max attempts:</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    className="designer-tool__input"
                    value={m.max_attempts || 2}
                    style={{ width: 50, fontSize: 12 }}
                    onChange={(e) => updateMarker(i, { max_attempts: parseInt(e.target.value) || 2 })}
                  />
                </div>
              </div>
            )}

            {!isEditing && q.prompt && (
              <div style={{ fontSize: 11, color: 'var(--fg-text-muted)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {q.prompt}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        className="designer-tool__button"
        onClick={addMarker}
        style={{ marginTop: 4 }}
      >
        + Add Checkpoint
      </button>
    </div>
  );
}

export const videoMCQMarker = new DesignerTool({
  id:         'platform.video_mcq_marker',
  label:      'Video Checkpoints',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: requiresSceneType('video'),
  Component:  VideoMCQMarker,
  weight:     55,
  helpText:   'Add checkpoint questions at specific timestamps. The video pauses and learners must answer to continue.',
});

// ────────────────────────────────────────────────────────────────────
// VideoZoneOverlay
// ────────────────────────────────────────────────────────────────────

function VideoZoneOverlay() {
  const { eventBus } = useDesigner();
  return (
    <div
      className="designer-tool designer-tool--canvas-overlay-toolbar"
      data-tool-id="platform.video_zone_overlay"
      role="toolbar"
      aria-label="Video tap-zone tools"
    >
      <button
        type="button"
        className="designer-tool__button designer-tool__button--icon"
        aria-label="Author tap zone"
        title="Author tap zone"
        onClick={() => eventBus.emit('video.zone_tool_changed', { tool: 'rect' })}
      >▭</button>
      <button
        type="button"
        className="designer-tool__button designer-tool__button--icon"
        aria-label="Clear tap zones"
        title="Clear zones"
        onClick={() => eventBus.emit('video.zone_tool_changed', { tool: 'clear' })}
      >⌫</button>
    </div>
  );
}

export const videoZoneOverlay = new DesignerTool({
  id:         'platform.video_zone_overlay',
  label:      'Tap zones',
  slot:       'CANVAS_OVERLAY',
  category:   'annotation',
  applies_to: requiresSceneType('video'),
  Component:  VideoZoneOverlay,
  weight:     55,
  helpText:   'Draw tap zones on the video frame for interactive moments.',
});

export const videoTools = [videoSourcePicker, videoMCQMarker, videoZoneOverlay];
export default videoTools;
